import { CompanySearchResult } from '@infodocs/shared/interfaces/company-search-result';

export interface ManualAddCompanyPrefill {
	companyName: string;
	registrationNumber: string;
}

const CIPC_REGISTRATION_NUMBER_PATTERN = /^\d{4}\/\d{6}\/\d{2}$/;

export function formatCipcRegistrationNumber(value: string): string {
	return value.trim().replace(/^K/i, '').replace(/\./g, '/');
}

export function isCipcRegistrationNumber(value: string): boolean {
	return CIPC_REGISTRATION_NUMBER_PATTERN.test(formatCipcRegistrationNumber(value));
}

export function resolveManualAddCompanyPrefill(
	query: string | null | undefined,
	results: CompanySearchResult[] = []
): ManualAddCompanyPrefill {
	const trimmedQuery = query?.trim() ?? '';
	if (!trimmedQuery) {
		return emptyPrefill();
	}

	const match = pickSearchResult(trimmedQuery, results);
	if (match) {
		return prefillFromSearchResult(match);
	}

	const formattedNumber = formatCipcRegistrationNumber(trimmedQuery);
	if (isCipcRegistrationNumber(formattedNumber)) {
		return { companyName: '', registrationNumber: formattedNumber };
	}

	return { companyName: trimmedQuery, registrationNumber: '' };
}

function emptyPrefill(): ManualAddCompanyPrefill {
	return { companyName: '', registrationNumber: '' };
}

function prefillFromSearchResult(result: CompanySearchResult): ManualAddCompanyPrefill {
	const info = result.CompanyInformation;

	return {
		companyName: info.CompanyName?.trim() ?? '',
		registrationNumber: formatCipcRegistrationNumber(info.CompanyRegistrationNumber ?? ''),
	};
}

function pickSearchResult(
	query: string,
	results: CompanySearchResult[]
): CompanySearchResult | undefined {
	return (
		findResultByRegistrationNumber(query, results) ??
		findResultByCompanyName(query, results) ??
		(results.length === 1 ? results[0] : undefined)
	);
}

function findResultByRegistrationNumber(
	query: string,
	results: CompanySearchResult[]
): CompanySearchResult | undefined {
	const formattedQuery = formatCipcRegistrationNumber(query);
	if (!isCipcRegistrationNumber(formattedQuery)) {
		return undefined;
	}

	return results.find(
		result =>
			formatCipcRegistrationNumber(result.CompanyInformation.CompanyRegistrationNumber) === formattedQuery
	);
}

function findResultByCompanyName(
	query: string,
	results: CompanySearchResult[]
): CompanySearchResult | undefined {
	const nameQuery = query.toLowerCase();
	const matches = results.filter(
		result => result.CompanyInformation.CompanyName?.trim().toLowerCase() === nameQuery
	);

	return matches.length === 1 ? matches[0] : undefined;
}
